import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';

import Layout from '../Layout/Layout';
import CompanyCertificationAdminPanel from './CompanyCertificationAdminPanel';
import ApplicationAdminPanel from './ApplicationAdminPanel';
import './adminStyles.css';
import AssessmentAdminPanel from './AssessmentAdminPanel';
import AssessorAdminPanel from './AssessorAdminPanel';
import { getAssessmentSurveyAssessmentsForDataGrid } from '../../services/assessmentSurveyAssessment';
import { NotificationImportantRounded } from '@mui/icons-material';
import CertificationsAdminPanel from './CertificationsAdminPanel';
import UsersAdminPanel from './UsersAdminPanel';
import BillingRecordsAdminPanel from './BillingRecordsAdminPanel';
import RemediationVerificationAdminPanel from './RemediationVerificationAdminPanel';
import { useAppSelector } from '../../app/hooks';
import { USER_TYPES } from '../../services/user';
import { LABELS } from '../../utils/labels';
import PartnerResourcesAdminPanel from './PartnerResourcesAdminPanel';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import WorkflowDisplayStatusVerification from './WorkflowDisplayStatusVerification';

const ADMIN_TABS = [
  { key: 'users', label: 'Users', component: UsersAdminPanel },
  { key: 'company-certifications', label: 'Company Certifications', component: CompanyCertificationAdminPanel },
  { key: 'applications', label: 'Applications', component: ApplicationAdminPanel },
  { key: 'assessments', label: 'Assessments', component: AssessmentAdminPanel },
  { key: 'assessors', label: 'Assessors', component: AssessorAdminPanel },
  { key: 'certifications', label: 'Certifications', component: CertificationsAdminPanel },
  { key: 'billing-records', label: 'Billing Records', component: BillingRecordsAdminPanel },
  { key: 'remediation-validation', label: LABELS.remediationValidation, component: RemediationVerificationAdminPanel },
  { key: 'partner-resources', label: 'Partner Resources', component: PartnerResourcesAdminPanel },
  { key: 'display-status-verification', label: 'Display Status Verification', component: WorkflowDisplayStatusVerification },
];

const DEFAULT_TAB = ADMIN_TABS[0].key;
const DEFAULT_CATEGORY = 'key-terms';
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>{children}</Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

export default function AdminTabs() {
  const [value, setValue] = useState(0);
  const [assessmentsAlert, setAssessmentsAlert] = useState(false);

  const { tab } = useParams<{ tab?: string }>();
  const { user } = useAppSelector(state => state.user);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const category = searchParams.get('category');
  const adminSection = tab && ADMIN_TABS.some(t => t.key === tab) ? tab : DEFAULT_TAB;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate(`/admin/${ADMIN_TABS[newValue].key}`);
  };

  const handleAssessmentsAddressed = () => setAssessmentsAlert(false);

  useEffect(() => {
    if (user?.type !== 'tpn_admin') return;

    getAssessmentSurveyAssessmentsForDataGrid({ page: 0, pageSize: 1 }).then((response) => {
      if (response.results.length > 0) setAssessmentsAlert(true);
    });
  }, []);

  useEffect(() => {
    const index = ADMIN_TABS.findIndex(t => t.key === adminSection);
    const safeIndex = index !== -1 ? index : 0;
    setValue(safeIndex);

    const urlBase = '/admin';
    const urlBody = ADMIN_TABS[safeIndex].key;
    const urlCategory =
      urlBody === 'partner-resources'
        ? `?category=${category ?? DEFAULT_CATEGORY}`
        : '';

    navigate(`${urlBase}/${urlBody}${urlCategory}`);
  }, [adminSection, category, navigate]);

  return (<>
    {user?.type ===  USER_TYPES.TPN_ADMIN && <Layout pageTitle="Admins" includeHeader={false} includeDivider={false}>
    <Tabs variant="scrollable" value={value} onChange={handleChange} aria-label="Company Admin Area">
      {ADMIN_TABS.map((t, idx) => (
        <Tab
          key={t.key}
          label={t.label}
          {...a11yProps(idx)}
          {...(t.key === 'assessments'
            ? {
              sx: { minHeight: '48px' },
              iconPosition: 'start' as const,
              icon: (
                  <>
                    {assessmentsAlert && (
                      <NotificationImportantRounded
                        color="warning"
                        sx={{
                          position: 'absolute',
                          top: '5px',
                          left: '0',
                          width: '.8em',
                        }}
                      />
                    )}
                  </>
              ),
            }
            : null)}
        />
      ))}
    </Tabs>
      <TabPanel value={value} index={0}>
        <UsersAdminPanel />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <CompanyCertificationAdminPanel />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <ApplicationAdminPanel />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <AssessmentAdminPanel assessmentsAddressed={handleAssessmentsAddressed} />
      </TabPanel>
      <TabPanel value={value} index={4}>
        <AssessorAdminPanel />
      </TabPanel>
      <TabPanel value={value} index={5}>
        <CertificationsAdminPanel />
      </TabPanel>
      <TabPanel value={value} index={6}>
        <BillingRecordsAdminPanel />
      </TabPanel>
      <TabPanel value={value} index={7}>
        <RemediationVerificationAdminPanel />
      </TabPanel>
      <TabPanel value={value} index={8}>
        < PartnerResourcesAdminPanel />
      </TabPanel>
      <TabPanel value={value} index={9}>
        <WorkflowDisplayStatusVerification />
      </TabPanel>
    </Layout>}</>
  );
}
